import { Injectable, inject } from '@angular/core'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { GROUP_KEYS_FUNCTION, PAST_DATE_RANGE_TYPES } from 'src/app/types/constants'
import { DateAxisType, DateRangeType } from 'src/app/types/enums'
import { LogService } from './log.service'
import { ChartConfig } from 'src/app/types/chart.model'
import { WEEK_START } from 'src/config/global-config'

dayjs.extend(utc)

// see https://js.devexpress.com/Angular/Documentation/ApiReference/Common_Types/charts/#TimeInterval
// not sure how to import
export type DxTimeInterval = 'year' | 'month' | 'week' | 'day'

@Injectable({
  providedIn: 'root',
})
export class DateUtilsService {
  private logger = inject(LogService);


  getDatasourceProviderGroupKeyFunc(
    isDateGrouping: boolean,
    dateAxisTypeId?: DateAxisType
  ): GROUP_KEYS_FUNCTION<any> {
    let groupKeyFunc: GROUP_KEYS_FUNCTION<any> = (items: any[], item: any, config: ChartConfig) => {
      return [item[config.argumentField]]
    }

    if (isDateGrouping) {
      switch (dateAxisTypeId) {
        case DateAxisType.Daily:
          groupKeyFunc = DateUtilsService.getDayKeyFunc
          break
        case DateAxisType.Weekly:
          groupKeyFunc = DateUtilsService.getWeekKeyFunc
          break
        case DateAxisType.Monthly:
          groupKeyFunc = DateUtilsService.getMonthKeyFunc
          break
        case DateAxisType.Yearly:
          groupKeyFunc = DateUtilsService.getYearKeyFunc
          break
        default:
          this.logger.log(
            'date-utils.service',
            'getDatasourceProviderGroupKeyFunc',
            null,
            'date grouping not implemented'
          )
          throw new Error('date grouping not implemented')
      }
    }

    return groupKeyFunc
  }

  getSimpleGroupKeyFunc(
    isDateGrouping: boolean,
    dateAxisTypeId?: DateAxisType
  ): (item: any) => any {
    let groupKeyFunc = (field: any) => {
      return field
    }

    if (isDateGrouping) {
      switch (dateAxisTypeId) {
        case DateAxisType.Daily:
          groupKeyFunc = DateUtilsService.getStartOfDay
          break
        case DateAxisType.Weekly:
          groupKeyFunc = DateUtilsService.getStartOfWeek
          break
        case DateAxisType.Monthly:
          groupKeyFunc = DateUtilsService.getStartOfMonth
          break
        case DateAxisType.Yearly:
          groupKeyFunc = DateUtilsService.getStartOfYear
          break
        default:
          this.logger.log(
            'date-utils.service',
            'getSimpleGroupKeyFunc',
            null,
            'date grouping not implemented'
          )
          throw new Error('date grouping not implemented')
      }
    }

    return groupKeyFunc
  }

  rangeAlignsToGrouping(grouping: DateAxisType, start: Date, end: Date): boolean {
    let period: dayjs.OpUnitType
    switch (grouping) {
      case DateAxisType.Daily:
        period = 'day'
        break
      case DateAxisType.Weekly:
        period = 'week'
        break
      case DateAxisType.Monthly:
        period = 'month'
        break
      case DateAxisType.Yearly:
        period = 'year'
        break
      default:
        this.logger.log(
          'date-utils.service',
          'rangeAlignsToGrouping',
          null,
          'date grouping not implemented'
        )
        throw new Error('date grouping not implemented')
    }

    const dayjsStart = dayjs(start).startOf('day')
    const dayjsEnd = dayjs(end).startOf('day')
    const startOfPeriod = dayjsStart.startOf(period)
    const endOfPeriod = dayjsEnd.endOf(period).startOf('day')

    return startOfPeriod.isSame(dayjsStart) && endOfPeriod.isSame(dayjsEnd)
  }

  // Ranges must have equal or larger period types than grouping
  // 'grouping' = DateAxisType string
  // 'range' = DateRangeType string
  isRangeCompatibleWithGrouping(grouping: string, range: string): boolean {
    if (!grouping || !range) {
      return true
    }

    const rangeLC = range.toLowerCase()
    if (rangeLC === 'custom' || rangeLC === 'all time') {
      return true
    }

    const groupingUnit = this.getPeriodUnitForRangesAndGrouping(grouping)
    const rangeUnit = this.getPeriodUnitForRangesAndGrouping(range)

    return rangeUnit >= groupingUnit
  }

  static isPresetDateRange(rangeType: DateRangeType): boolean {
    if (rangeType == null) return false
    return rangeType !== DateRangeType['Custom'] && rangeType !== DateRangeType['All Time']
  }

  static isPresetDateRangeStr(rangeType: string): boolean {
    if (rangeType == null) return false
    return (
      rangeType !== DateRangeType[DateRangeType['All Time']] &&
      rangeType !== DateRangeType[DateRangeType['Custom']]
    )
  }

  private static calculateDateRange(
    rangeUnit: dayjs.ManipulateType, // 'week' | 'month' | 'year'
    amount: number,
    direction: 'last' | 'next',
    includeCurrentPartialPeriod: boolean,
    grouping: DateAxisType, // cannot be larger than rangeUnit
    argumentFieldIsDate: boolean
  ): [dayjs.Dayjs, dayjs.Dayjs] {
    const now = dayjs.utc().startOf('day')
    // if grouping by date, we want to use the grouping period type
    // to snap the start and end dates to the group periods:
    const opUnit = (
      !argumentFieldIsDate
        ? rangeUnit
        : grouping === DateAxisType.Weekly
        ? 'week'
        : grouping === DateAxisType.Monthly
        ? 'month'
        : grouping === DateAxisType.Yearly
        ? 'year'
        : grouping === DateAxisType.Daily
        ? 'day'
        : rangeUnit
    ) as dayjs.OpUnitType

    if (direction === 'last') {
      const end = includeCurrentPartialPeriod
        ? now
        : now.subtract(1, rangeUnit)
          .endOf(rangeUnit)
          .endOf(opUnit) // if opUnit diff to rangeUnit, ensure we get full last period ('snap' to the grouping period end)
          .startOf('day')

      const start = end
        .subtract(amount - 1, rangeUnit)
        .startOf(rangeUnit)
        .startOf(opUnit) // if opUnit diff to unit, ensure we get full first period ('snap' to the grouping period start)
      
      return [start, end]
    } else { // direction = 'next'
      // If today is the first day of the period, we're at a full period - no need to skip ahead
      const isFirstDayOfPeriod = now.isSame(now.startOf(rangeUnit), 'day')
      
      const start = includeCurrentPartialPeriod || isFirstDayOfPeriod
        ? now.startOf(opUnit)
        : now.add(1, rangeUnit)
        .startOf(rangeUnit)
        .startOf(opUnit) // if opUnit diff to unit, ensure we get full first period ('snap' to the grouping period start)

      const end = start
        .add(amount, rangeUnit)
        .subtract(1, 'day')
        .endOf(opUnit) // if opUnit diff to rangeUnit, ensure we get full last period ('snap' to the grouping period end)
        .startOf('day')
      
      return [start, end]
    }
  }

  static getGroupingStartAndEndDate(
    rangeType: DateRangeType,
    includeCurrentPartialPeriod: boolean,
    grouping: DateAxisType,
    argumentFieldIsDate: boolean
  ): [dayjs.Dayjs, dayjs.Dayjs] {
    switch (rangeType) {
      case DateRangeType.Custom:
      case DateRangeType['All Time']:
        throw Error('attempting to get dates for all-time or custom date range')

      case DateRangeType['Last 5 Years']:
        return this.calculateDateRange('year', 5, 'last', includeCurrentPartialPeriod, grouping, argumentFieldIsDate)
      case DateRangeType['Last Year']:
        return this.calculateDateRange('year', 1, 'last', includeCurrentPartialPeriod, grouping, argumentFieldIsDate)
      case DateRangeType['Last 12 Months']:
        return this.calculateDateRange('month', 12, 'last', includeCurrentPartialPeriod, grouping, argumentFieldIsDate)
      case DateRangeType['Last 6 Months']:
        return this.calculateDateRange('month', 6, 'last', includeCurrentPartialPeriod, grouping, argumentFieldIsDate)
      case DateRangeType['Last 3 Months']:
        return this.calculateDateRange('month', 3, 'last', includeCurrentPartialPeriod, grouping, argumentFieldIsDate)
      case DateRangeType['Last Month']:
        return this.calculateDateRange('month', 1, 'last', includeCurrentPartialPeriod, grouping, argumentFieldIsDate)
      case DateRangeType['Last 2 Weeks']:
        return this.calculateDateRange('week', 2, 'last', includeCurrentPartialPeriod, grouping, argumentFieldIsDate)
      case DateRangeType['Last 4 Weeks']:
        return this.calculateDateRange('week', 4, 'last', includeCurrentPartialPeriod, grouping, argumentFieldIsDate)
      case DateRangeType['Last 8 Weeks']:
        return this.calculateDateRange('week', 8, 'last', includeCurrentPartialPeriod, grouping, argumentFieldIsDate)
      case DateRangeType['Last 12 Weeks']:
        return this.calculateDateRange('week', 12, 'last', includeCurrentPartialPeriod, grouping, argumentFieldIsDate)

      case DateRangeType['Next 5 Years']:
        return this.calculateDateRange('year', 5, 'next', includeCurrentPartialPeriod, grouping, argumentFieldIsDate)
      case DateRangeType['Next Year']:
        return this.calculateDateRange('year', 1, 'next', includeCurrentPartialPeriod, grouping, argumentFieldIsDate)
      case DateRangeType['Next 6 Months']:
        return this.calculateDateRange('month', 6, 'next', includeCurrentPartialPeriod, grouping, argumentFieldIsDate)
      case DateRangeType['Next 3 Months']:
        return this.calculateDateRange('month', 3, 'next', includeCurrentPartialPeriod, grouping, argumentFieldIsDate)
      case DateRangeType['Next Month']:
        return this.calculateDateRange('month', 1, 'next', includeCurrentPartialPeriod, grouping, argumentFieldIsDate)
      case DateRangeType['Next 2 Weeks']:
        return this.calculateDateRange('week', 2, 'next', includeCurrentPartialPeriod, grouping, argumentFieldIsDate)
      case DateRangeType['Next 4 Weeks']:
        return this.calculateDateRange('week', 4, 'next', includeCurrentPartialPeriod, grouping, argumentFieldIsDate)
      case DateRangeType['Next 8 Weeks']:
        return this.calculateDateRange('week', 8, 'next', includeCurrentPartialPeriod, grouping, argumentFieldIsDate)
      case DateRangeType['Next 12 Weeks']:
        return this.calculateDateRange('week', 12, 'next', includeCurrentPartialPeriod, grouping, argumentFieldIsDate)

      default:
        console.error('undefined range type in getGroupingStartAndEndDate', rangeType)
        throw Error('undefined range type')
    }
  }

  getDateRangeHTMLWithDates(
    dateRangeType: DateRangeType,
    includeCurrentPartialPeriod: boolean,
    dateFrom: Date | undefined,
    dateTo: Date | undefined,
    grouping: DateAxisType,
    argumentFieldIsDate: boolean
  ) {
    if (dateRangeType == null || dateRangeType === DateRangeType['All Time']) return 'All time'

    if (dateRangeType === DateRangeType['Custom']) return this.getRangeText(dateFrom, dateTo)

    let rangeText: string
    const dates = DateUtilsService.getGroupingStartAndEndDate(
      dateRangeType,
      includeCurrentPartialPeriod,
      grouping,
      argumentFieldIsDate
    )

    if (PAST_DATE_RANGE_TYPES.includes(DateRangeType[dateRangeType])) {
      if (includeCurrentPartialPeriod) {
        rangeText = `${this.formatDateStandard(dates[0].toDate())} -> today`
      } else {
        rangeText = `${this.formatDateStandard(dates[0].toDate())} -> ${this.formatDateStandard(
          dates[1].toDate()
        )}`
      }
    }
    // future date range type
    else
      rangeText = `${this.formatDateStandard(dates[0].toDate())} -> ${this.formatDateStandard(
        dates[1].toDate()
      )}`

    const rangeSuffix = `<span class="smaller">(${rangeText})</span>`

    switch (dateRangeType) {
      case DateRangeType['Last 5 Years']:
        return `Last 5 Years ${rangeSuffix}`
      case DateRangeType['Last Year']:
        return `Last Year ${rangeSuffix}`
      case DateRangeType['Last 12 Months']:
        return `Last 12 months ${rangeSuffix}`
      case DateRangeType['Last 6 Months']:
        return `Last 6 months ${rangeSuffix}`
      case DateRangeType['Last 3 Months']:
        return `Last 3 Months ${rangeSuffix}`
      case DateRangeType['Last Month']:
        return `Last Month ${rangeSuffix}`
      case DateRangeType['Last 2 Weeks']:
        return `Last 2 Weeks ${rangeSuffix}`
      case DateRangeType['Last 4 Weeks']:
        return `Last 4 Weeks ${rangeSuffix}`
      case DateRangeType['Last 8 Weeks']:
        return `Last 8 Weeks ${rangeSuffix}`
      case DateRangeType['Last 12 Weeks']:
        return `Last 12 Weeks ${rangeSuffix}`
      case DateRangeType['Next 5 Years']:
        return `Next 5 Years ${rangeSuffix}`
      case DateRangeType['Next Year']:
        return `Next Year ${rangeSuffix}`
      case DateRangeType['Next 6 Months']:
        return `Next 6 Months ${rangeSuffix}`
      case DateRangeType['Next 3 Months']:
        return `Next 3 Months ${rangeSuffix}`
      case DateRangeType['Next Month']:
        return `Next Month ${rangeSuffix}`
      case DateRangeType['Next 2 Weeks']:
        return `Next 2 Weeks ${rangeSuffix}`
      case DateRangeType['Next 4 Weeks']:
        return `Next 4 Weeks ${rangeSuffix}`
      case DateRangeType['Next 8 Weeks']:
        return `Next 8 Weeks ${rangeSuffix}`
      case DateRangeType['Next 12 Weeks']:
        return `Next 12 Weeks ${rangeSuffix}`
      default:
        this.logger.log(
          'date-utils.service',
          'getDateRangeHTMLWithDates',
          null,
          'unhandled date range type'
        )
        throw Error('unhandled date range type in getDateRangeHTMLWithDates')
    }
  }

  static getDayjsUTCDateWithoutTimeFromDate(
    dateStr: dayjs.Dayjs | Date | string | null | undefined
  ): dayjs.Dayjs | null {
    if (!dateStr) return null

    // Force formatting to YYYY-MM-DD first.
    // This strips time and timezone info, locking in the "Calendar Date".
    // Then we create a clean UTC entry for that specific date.
    return dayjs.utc(dayjs(dateStr).format('YYYY-MM-DD'))
  }

  /** zeroes time - assumes date is correct regardless of timezone */
  UTCStringWithoutTimeFromDate(dateStr: Date | string): string {
    const date = new Date(dateStr)
    const twoDigitDate = ('0' + date.getDate()).slice(-2)
    const twoDigitMonth = ('0' + (date.getMonth() + 1)).slice(-2)
    return `${date.getFullYear()}-${twoDigitMonth}-${twoDigitDate}T00:00:00Z`
  }

  /** e.g. '1/5/2022' */
  formatDateStandard(date: Date | string | undefined): string {
    if (date == null) {
      this.logger.log('date-utils.service', 'formatDateStandard', null, 'no date')
      throw Error('no date in formatDateStandard')
    }

    if (typeof date === 'string') date = new Date(date)

    return `${(date as Date).getDate()}/${(date as Date).getMonth() + 1}/${(
      date as Date
    ).getFullYear()}`
  }

  /** e.g. '01/05/2022' */
  formatDatePadded(date: Date | string | undefined): string {
    if (date == null) {
      this.logger.log('date-utils.service', 'formatDateStandard', null, 'no date')
      throw Error('no date in formatDateStandard')
    }

    if (typeof date === 'string') date = new Date(date)

    return date.toLocaleDateString('en-au')
  }

  /** 'Month YYYY' */
  static getDateMonthString(date: Date): string {
    return date.toLocaleString('default', { month: 'long', year: 'numeric' })
  }

  static getDateYearString(date: Date): string {
    return date.getFullYear().toString()
  }

  getDateGroupingPeriodName(grouping: DateAxisType): DxTimeInterval {
    switch (grouping) {
      case DateAxisType.Daily:
        return 'day'
      case DateAxisType.Yearly:
        return 'year'
      case DateAxisType.Monthly:
        return 'month'
      case DateAxisType.Weekly:
        return 'week'
      default:
        this.logger.log(
          'date-utils.service',
          'getDateGroupingPeriodName',
          null,
          'unhandled date axis grouping'
        )
        throw new Error('unhandled date axis grouping in chart')
    }
  }

  getDayJsPeriodName(grouping: DateAxisType): dayjs.ManipulateType {
    switch (grouping) {
      case DateAxisType.Weekly:
        return 'week'
      default:
        return this.getDateGroupingPeriodName(grouping).toLowerCase() as dayjs.ManipulateType
    }
  }

  static getPeriodKeysBetween(one: Date, two: Date, periodType: DateAxisType) {
    switch (periodType) {
      case DateAxisType.Daily:
        return DateUtilsService.getDayKeysBetween(one, two)
      case DateAxisType.Weekly:
        return DateUtilsService.getWeekKeysBetween(one, two)
      case DateAxisType.Monthly:
        return DateUtilsService.getMonthKeysBetween(one, two)
      case DateAxisType.Yearly:
        return DateUtilsService.getYearKeysBetween(one, two)
      default:
        throw new Error('date grouping not implemented')
    }
  }

  static formatDateLabel(date: Date, grouping: DateAxisType): string {
    switch (grouping) {
      case DateAxisType.Daily:
        return date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })
      case DateAxisType.Weekly:
        return 'W/C ' + date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })
      case DateAxisType.Monthly:
        return this.getDateMonthString(date)
      case DateAxisType.Yearly:
        return this.getDateYearString(date)
      default:
        return date.toLocaleDateString('en-AU')
    }
  }

  private static getDayKeysBetween(dateOne: Date, dateTwo: Date): Date[] {
    const timeDiff = Math.abs(dateOne.getTime() - dateTwo.getTime())
    const daysBetween = Math.ceil(timeDiff / (1000 * 3600 * 24))

    const keys = []
    const firstDay = this.getStartOfDay(dateOne)
    const start = dayjs(firstDay)
    keys.push(firstDay)
    for (let index = 1; index <= daysBetween; index++) {
      const next = start.add(index, 'days')
      keys.push(next.toDate())
    }

    return keys
  }

  private static getWeekKeysBetween(dateOne: Date, dateTwo: Date): Date[] {
    const week = 7 * 24 * 60 * 60 * 1000
    const startWeek1 = DateUtilsService.getStartOfWeek(dateOne)
    const startWeek2 = DateUtilsService.getStartOfWeek(dateTwo)
    const weeksBetween = Math.ceil((startWeek2.getTime() - dateOne.getTime()) / week)

    const keys = []
    const start = dayjs(startWeek1)
    keys.push(startWeek1)
    for (let index = 1; index <= weeksBetween; index++) {
      const next = start.add(index, 'weeks')
      keys.push(next.toDate())
    }

    return keys
  }

  private static getMonthKeysBetween(dateOne: Date, dateTwo: Date): Date[] {
    const monthsBetween =
      (dateTwo.getFullYear() - dateOne.getFullYear()) * 12 +
      (dateTwo.getMonth() - dateOne.getMonth())

    const keys = []
    const firstMonth = this.getStartOfMonth(dateOne)
    const start = dayjs(firstMonth)
    keys.push(firstMonth)
    for (let index = 1; index <= monthsBetween; index++) {
      const next = start.add(index, 'months')
      keys.push(next.toDate())
    }

    return keys
  }

  private static getYearKeysBetween(dateOne: Date, dateTwo: Date): Date[] {
    const yearsBetween = dateTwo.getFullYear() - dateOne.getFullYear()

    const keys = []
    const firstMonth = this.getStartOfYear(dateOne)
    const start = dayjs(firstMonth)
    keys.push(firstMonth)
    for (let index = 1; index <= yearsBetween; index++) {
      const next = start.add(index, 'years')
      keys.push(next.toDate())
    }

    return keys
  }

  private getRangeText(dateFrom: Date | undefined, dateTo: Date | undefined) {
    if (!dateFrom && !dateTo) {
      this.logger.log('date-utils.service', 'getRangeText', null, 'no start or end date')
      throw Error('no start or end date in getCustomRangeText')
    }

    return !dateFrom
      ? 'Everything to ' + this.formatDateStandard(dateTo!)
      : !dateTo
      ? 'Everything from ' + this.formatDateStandard(dateFrom)
      : this.formatDateStandard(dateFrom) + ' -> ' + this.formatDateStandard(dateTo)
  }

  private static getDayKeyFunc = (items: any[], item: any, config: ChartConfig): Date[] => {
    const date: Date = item[config.argumentField]
    return [DateUtilsService.getStartOfDay(date)]
  }

  private static getWeekKeyFunc = (items: any[], item: any, config: ChartConfig): Date[] => {
    const date: Date = item[config.argumentField]
    return [DateUtilsService.getStartOfWeek(date)]
  }

  private static getMonthKeyFunc = (items: any[], item: any, config: ChartConfig): Date[] => {
    const date: Date = item[config.argumentField]
    return [DateUtilsService.getStartOfMonth(date)]
  }

  private static getYearKeyFunc = (items: any[], item: any, config: ChartConfig): Date[] => {
    const date: Date = item[config.argumentField]
    return [DateUtilsService.getStartOfYear(date)]
  }

  private static getStartOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate())
  }

  private static getStartOfWeek(date: Date | string): Date {
    const dateObj = typeof date === 'string' ? new Date(date) : date

    if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
      throw new Error('Invalid date provided to getStartOfWeek: ' + date)
    }

    const copy = new Date(dateObj)
    while (copy.getDay() != WEEK_START) {
      copy.setDate(copy.getDate() - 1)
    }

    // 'May W2 2022' string format
    // const firstWeekday = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    // const offsetDate = date.getDate() + firstWeekday - 1;
    // const weekNum = Math.floor(offsetDate / 7) + 1;
    // return `${month} W${weekNum} ${date.getUTCFullYear()}`;

    return new Date(copy.getFullYear(), copy.getMonth(), copy.getDate())
  }

  private static getStartOfMonth(date: Date | string): Date {
    const dateObj = typeof date === 'string' ? new Date(date) : date

    if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
      throw new Error('Invalid date provided to getStartOfMonth: ' + date)
    }

    return new Date(dateObj.getFullYear(), dateObj.getMonth(), 1)
  }

  private static getStartOfYear(date: Date | string): Date {
    const dateObj = typeof date === 'string' ? new Date(date) : date

    if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
      throw new Error('Invalid date provided to getStartOfYear: ' + date)
    }

    return new Date(dateObj.getFullYear(), 0, 1)
  }

  // rangeOrGrouping can be DateRangeType or DateAxisType string
  private getPeriodUnitForRangesAndGrouping(rangeOrGrouping: string): number {
    const sLC = rangeOrGrouping.toLowerCase()
    if (sLC.includes('day') || sLC.includes('daily')) return 1

    if (sLC.includes('week')) return 2

    if (sLC.includes('month')) return 3

    if (sLC.includes('year')) return 4

    throw Error('unhandled period unit in getPeriodUnit')
  }
}
